import {
  ProgressScreen,
  type ProgressStep
} from '@/components/progress-screen';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useRollbackAndReplace } from '@/hooks/use-rollback-and-replace';
import { useSSE } from '@/hooks/use-sse';
import { generateAPIUrl } from '@/utils/api-url';
import {
  type CombinedSteps,
  PROCESS_STEPS
} from '@/utils/persona-import-steps';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { Fragment, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const initialSteps = PROCESS_STEPS.map((step) => ({
  stepType: step.stepType,
  completed: false
}));

export default function PersonaProcess() {
  const { id } = useLocalSearchParams();

  const [processProgress, setProcessProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [processStatus, setProcessStatus] = useState<
    'pending' | 'in-progress' | 'completed' | 'failed' | null
  >(null);
  const [processStepDetails, setProcessStepDetails] =
    useState<ProgressStep[]>(initialSteps);

  const router = useRouter();
  const rollbackAndReplace = useRollbackAndReplace();

  useSSE<{
    personaImport: {
      status: 'pending' | 'in-progress' | 'completed' | 'failed';
    };
    combinedSteps: CombinedSteps;
  }>(
    generateAPIUrl(`/api/persona-import/${id}/process-status`),
    (event) => {
      setProcessStatus(event.personaImport.status);
      setProcessStepDetails(event.combinedSteps.stepDetails);
      setProcessProgress(event.combinedSteps.progress);

      if (event.combinedSteps.errors.length > 0) {
        setError(event.combinedSteps.errors.join('\n'));
        return false;
      }
      if (event.personaImport.status === 'failed') {
        setError('Processing failed. Please try again.');
        return false;
      }

      return true;
    },
    (sseError) => {
      setError(sseError.message);
    }
  );

  return (
    <Fragment>
      <Head>
        <title>Persona Processing - Lexikon AI</title>
      </Head>
      <SafeAreaView edges={['right', 'bottom', 'left']}>
        <ScrollView contentContainerClassName="flex">
          <View className="px-12 pt-4">
            <View className="web:overflow-y-auto web:pb-4 web:lg:w-[50%] web:lg:self-center">
              {error ? (
                <View className="gap-4 pt-4">
                  <Text>Processing failed. Please try again.</Text>
                  <Text>{error}</Text>
                  <Button
                    variant="default"
                    onPress={() => {
                      router.replace(`/persona-import/process/${id}`);
                    }}
                  >
                    <Text>Refresh</Text>
                  </Button>
                </View>
              ) : (
                <Fragment>
                  <ProgressScreen
                    progress={processProgress}
                    progressText="Processing your Persona"
                    completed={processStatus === 'completed'}
                    completedText="Processing complete!"
                    progressSteps={processStepDetails}
                  />
                  {processStatus === 'completed' ? (
                    <Button
                      variant="default"
                      className="mt-4 px-4"
                      onPress={() => {
                        rollbackAndReplace(`/app?personaImportId=${id}`);
                      }}
                    >
                      <Text>Continue</Text>
                    </Button>
                  ) : null}
                </Fragment>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Fragment>
  );
}
